import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers';
import { PLATFORM_SUMMARY_QUERY_KEY } from '@/state/queries/ops-admin/use-platform-summary';
import { ATTENTION_QUEUE_QUERY_KEY } from '@/state/queries/ops-admin/use-attention-queue';
import { LIST_PROCESSOR_POOL_QUERY_KEY } from '@/state/queries/processor-admin/use-list-processor-pool';
import { LIST_MEDIA_AGENT_BOOKINGS_QUERY_KEY } from '@/state/queries/media-agent-admin/use-list-media-agent-bookings';
import { LIST_MEDIA_AGENT_POOL_QUERY_KEY } from '@/state/queries/media-agent-admin/use-list-media-agent-pool';

function invalidateOpsQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [PLATFORM_SUMMARY_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [ATTENTION_QUEUE_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [LIST_PROCESSOR_POOL_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_AGENT_BOOKINGS_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: [LIST_MEDIA_AGENT_POOL_QUERY_KEY] });
}

export function useOpsReleaseProcessorMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerId: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/ops/actions/release-processor`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: { playerId },
        token: token ?? '',
      }) as Promise<{ requeued: number; warnings?: string[] }>;
    },
    onSuccess: () => invalidateOpsQueries(queryClient),
  });
}

export function useOpsResetProcessorMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playerId: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/ops/actions/reset-processor`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: { playerId },
        token: token ?? '',
      }) as Promise<{ requeued: number; resetEmitted: boolean; warnings?: string[] }>;
    },
    onSuccess: () => invalidateOpsQueries(queryClient),
  });
}

export function useOpsRequeuePreprocessMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { mediaId: string; assetVersion: number }) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/ops/actions/requeue-preprocess`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: params,
        token: token ?? '',
      }) as Promise<{ result: string }>;
    },
    onSuccess: () => invalidateOpsQueries(queryClient),
  });
}

export function useOpsRetryFailedPreprocessMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { mediaId: string; assetVersion: number }) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/ops/actions/retry-failed-preprocess`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: params,
        token: token ?? '',
      }) as Promise<{ result: string }>;
    },
    onSuccess: () => invalidateOpsQueries(queryClient),
  });
}

export function useOpsResetMediaAgentBookingMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingId: string) => {
      const token = await getToken();
      const url = `${import.meta.env.VITE_API_URL}/ops/actions/reset-media-agent-booking`;
      return sendJSONRequest({
        url,
        method: METHOD.POST,
        body: { bookingId },
        token: token ?? undefined,
      }) as Promise<{ bookingId: string; status: string }>;
    },
    onSuccess: () => invalidateOpsQueries(queryClient),
  });
}
